import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/stores/auth";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  Bot,
  RefreshCw,
  Search,
  Sparkles,
  MessageSquare,
  Image,
  Code2,
  AudioLines,
} from "lucide-react";

interface Channel {
  id: number;
  name: string;
  type: number;
  status: number;
  group: string;
  weight: number;
  balance: number;
  models: string;
}

interface ModelData {
  model: string;
  channels: Channel[];
  total_channels: number;
}

const MODEL_ICON_MAP: Record<string, typeof Bot> = {
  "gpt": MessageSquare,
  "claude": Sparkles,
  "dall": Image,
  "stable": Image,
  "midjourney": Image,
  "code": Code2,
  "whisper": AudioLines,
  "tts": AudioLines,
};

function getModelIcon(modelName: string) {
  const lower = modelName.toLowerCase();
  for (const [key, Icon] of Object.entries(MODEL_ICON_MAP)) {
    if (lower.includes(key)) return Icon;
  }
  return Bot;
}

function getModelColor(modelName: string): string {
  const lower = modelName.toLowerCase();
  if (lower.includes("gpt-4o")) return "from-violet-500 to-purple-600";
  if (lower.includes("gpt-4")) return "from-blue-500 to-indigo-600";
  if (lower.includes("gpt-3")) return "from-emerald-500 to-teal-600";
  if (lower.includes("claude")) return "from-orange-500 to-amber-600";
  if (lower.includes("dall") || lower.includes("stable") || lower.includes("midjourney")) return "from-pink-500 to-rose-600";
  if (lower.includes("whisper") || lower.includes("tts")) return "from-cyan-500 to-sky-600";
  return "from-gray-500 to-slate-600";
}

export function AvailableModelsPage() {
  const { accessToken } = useAuthStore();
  const [models, setModels] = useState<ModelData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchAvailableModels = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/channels/available-models", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || "获取可用模型失败");
      }

      setModels(result.data || []);
    } catch (err: any) {
      console.error("获取可用模型失败:", err);
      setError(err.message || "获取可用模型失败");
      toast.error(err.message || "获取可用模型失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredModels = useMemo(() => {
    if (!searchQuery.trim()) return models;
    const q = searchQuery.toLowerCase();
    return models.filter((m) => m.model.toLowerCase().includes(q));
  }, [models, searchQuery]);

  return (
    <LayoutWithSidebar>
      <div className="flex flex-col gap-6 p-6 max-w-4xl mx-auto w-full">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">可用模型</h1>
          <p className="text-gray-500 mt-1">当前系统支持的 AI 模型列表</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索模型名称..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
          </div>
          <Button
            onClick={fetchAvailableModels}
            disabled={loading}
            variant="outline"
            size="sm"
            className="gap-1.5 shrink-0"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            刷新
          </Button>
        </div>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-700 text-sm">{error}</p>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <p className="ml-3 text-gray-500">加载中...</p>
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="text-center py-20">
            <Bot className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400">
              {searchQuery ? "没有匹配的模型" : "暂无可用模型"}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>共 {filteredModels.length} 个模型可用</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredModels.map((modelData) => {
                const Icon = getModelIcon(modelData.model);
                const gradient = getModelColor(modelData.model);
                return (
                  <div
                    key={modelData.model}
                    className="group relative bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:border-gray-200 transition-all duration-200"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2.5 rounded-lg bg-gradient-to-br ${gradient} shrink-0`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm leading-tight truncate">
                          {modelData.model}
                        </h3>
                        <div className="mt-1.5">
                          <Badge
                            variant="outline"
                            className="text-xs bg-green-50 text-green-700 border-green-200"
                          >
                            可用
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </LayoutWithSidebar>
  );
}

export default AvailableModelsPage;
